import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ShoppingCart as CartIcon, Download, CreditCard, Mail } from 'lucide-react';
import axios from 'axios';
import { PageRenderer } from '@/components/PageRenderer';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';

export const Help = () => {
  return (
    <PageEditProvider slug="help">
      <HelpInner />
      <PageEditToolbar />
    </PageEditProvider>
  );
};

const HelpInner = () => {
  const [pageBlocks, setPageBlocks] = useState([]);

  useEffect(() => {
    loadPageContent();
  }, []);

  const loadPageContent = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pages/help`);
      if (response.data?.blocks?.length > 0) {
        setPageBlocks(response.data.blocks);
      }
    } catch (error) {
      // Page content not found, use default
      console.log('Using default help page content');
    }
  };

  // If CMS blocks exist, render them instead
  if (pageBlocks.length > 0) {
    return <PageRenderer blocks={pageBlocks} />;
  }

  const faqs = [
    {
      icon: CartIcon,
      question: 'How do I buy music?',
      answer: 'Simply browse our collection, tap a track or album, and click "Add to Cart". When ready, proceed to checkout to complete your purchase securely through Stripe.',
    },
    {
      icon: Download,
      question: 'How do I download my music?',
      answer: 'After payment, your download starts instantly. You\'ll also receive download links via email. All purchased music is available in your "My Library" section anytime.',
    },
    {
      icon: HelpCircle,
      question: 'Can I preview music before buying?',
      answer: 'Yes! Every track has a preview player. Just click the play button on any track card to listen to a 30-60 second sample before purchasing.',
    },
    {
      icon: CreditCard,
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, Amex), Apple Pay, and Google Pay. All payments are processed securely through Stripe with the highest security standards.',
    },
    {
      icon: Mail,
      question: 'Will I receive a receipt?',
      answer: 'Yes, you\'ll receive an email receipt immediately after purchase with your order details and download links. Keep this email for your records.',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-center mb-16">
          <HelpCircle className="w-16 h-16 text-primary mx-auto mb-6" />
          <PageEditable
            path="hero.title"
            as="h1"
            className="text-4xl md:text-5xl font-bold font-display text-foreground mb-4"
            fallback="How Can We Help?"
            data-testid="help-title"
          />
          <PageEditable
            path="hero.subtitle"
            as="p"
            multiline
            className="text-lg text-muted-foreground"
            fallback="Find answers to common questions about Escola de Samba Casa Samba Afro-Brasilian Cultural School"
          />
        </div>

        <div className="space-y-6">
          {faqs.map((faq, index) => {
            const Icon = faq.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-card border border-border/50 rounded-2xl p-8 hover:border-primary/30 transition-all"
                data-testid={`faq-item-${index}`}
              >
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <PageEditable
                      path={`faqs.${index}.question`}
                      as="h3"
                      className="text-xl font-bold text-foreground mb-3"
                      fallback={faq.question}
                    />
                    <PageEditable
                      path={`faqs.${index}.answer`}
                      as="p"
                      multiline
                      className="text-muted-foreground leading-relaxed"
                      fallback={faq.answer}
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Payment Security Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12 bg-stone-900/50 backdrop-blur-md border border-primary/20 rounded-3xl p-8"
          data-testid="payment-security-info"
        >
          <PageEditable
            path="security.title"
            as="h2"
            className="text-2xl font-bold font-display text-foreground mb-4"
            fallback="Secure Payment Information"
          />
          <PageEditable
            path="security.description"
            as="p"
            multiline
            className="text-muted-foreground leading-relaxed"
            fallback="Payments on Escola de Samba Casa Samba are processed securely, supporting major credit cards and digital wallets like Apple Pay and Google Pay where available. Your transactions are protected with the highest security standards, ensuring your information is safe. After payment, you gain instant access to your digital purchases for a seamless experience."
          />
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-12 text-center"
        >
          <PageEditable
            path="stillHave.title"
            as="h3"
            className="text-xl font-bold text-foreground mb-3"
            fallback="Still have questions?"
          />
          <PageEditable
            path="stillHave.subtitle"
            as="p"
            className="text-muted-foreground mb-6"
            fallback="We're here to help! Feel free to reach out to us."
          />
          <a
            href="mailto:support@casasamba.com"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105 shadow-glow"
            data-testid="contact-support-btn"
          >
            <Mail className="w-5 h-5" />
            <PageEditable path="stillHave.cta" fallback="Contact Support" />
          </a>
        </motion.div>
      </motion.div>
    </div>
  );
};